import React from 'react';
import { FinanceOffer } from '../../types/catalog';
import { termOptions } from '../../data/options';
import { Field } from '../ui/Field';
import { NumberInput } from './NumberInput';
interface OfferEditorProps {
  value: FinanceOffer;
  onChange: (o: FinanceOffer) => void;
  idPrefix: string;
}
export function OfferEditor({
  value,
  onChange,
  idPrefix
}: OfferEditorProps) {
  const set = <K extends keyof FinanceOffer,>(k: K, v: FinanceOffer[K]) => onChange({
    ...value,
    [k]: v
  });
  const toggleTerm = (t: number) => set('terms', value.terms.includes(t) ? value.terms.filter(x => x !== t) : [...value.terms, t].sort((a, b) => a - b));
  const p = idPrefix;
  return <div className="space-y-4" data-id="element-178" data-mp="pr4ttwhwc4o4">
      <div className="grid gap-4 sm:grid-cols-3" data-id="element-179" data-mp="pq3fnj5l3226">
        <Field label="TNA (%)" htmlFor={`${p}-tna`} hint="Tasa nominal anual informada por la entidad." data-id="element-180" data-mp="pszfjd0zs0t9">
          <NumberInput id={`${p}-tna`} value={value.tna} onChange={v => set('tna', v)} allowNull step={0.01} data-id="element-181" data-mp="3ahdn3g91jwq3" />
        </Field>
        <Field label="CFT (%)" htmlFor={`${p}-cft`} hint="Sólo si la entidad lo informó. No es la TNA." data-id="element-182" data-mp="3p5pkv398a6b7">
          <NumberInput id={`${p}-cft`} value={value.cft} onChange={v => set('cft', v)} allowNull step={0.01} data-id="element-183" data-mp="3k87i5ult0nbn" />
        </Field>
        <Field label="Sistema de amortización" htmlFor={`${p}-amort`} data-id="element-184" data-mp="30voldptpc5q">
          <select id={`${p}-amort`} className="input" value={value.amortization} onChange={e => set('amortization', e.target.value as FinanceOffer['amortization'])} data-id="element-185" data-mp="3u4kdewwzz98i">
            <option value="frances" data-id="element-186" data-mp="715ukqdm82kl">Francés (cuota fija)</option>
            <option value="aleman" data-id="element-187" data-mp="70mdtczpbk85">Alemán (cuota decreciente)</option>
          </select>
        </Field>
      </div>

      <fieldset data-id="element-188" data-mp="lrmvm8mc8qgy">
        <legend className="label" data-id="element-189" data-mp="gqz5dp5xodns">Plazos habilitados (meses)</legend>
        <div className="flex flex-wrap gap-2" data-id="element-190" data-mp="gsserc7xlbzh">
          {termOptions.map(t => <label key={t} className={`cursor-pointer rounded-lg border px-3 py-1.5 text-sm transition-colors duration-150 ${value.terms.includes(t) ? 'border-neon bg-neon/10 text-white' : 'border-line text-mist'}`} data-id="element-191" data-mp="ur09ddhhw17h">
              <input type="checkbox" className="sr-only" checked={value.terms.includes(t)} onChange={() => toggleTerm(t)} data-id="element-192" data-mp="xpz0bxatandx" />
              {t}
            </label>)}
        </div>
      </fieldset>

      <div className="grid gap-4 sm:grid-cols-2" data-id="element-193" data-mp="17oephuqa3mks">
        <Field label="Anticipo mínimo (%)" htmlFor={`${p}-down`} data-id="element-194" data-mp="1ci143sg060j8">
          <NumberInput id={`${p}-down`} value={value.minDownPct} onChange={v => set('minDownPct', Math.min(100, v ?? 0))} data-id="element-195" data-mp="1gitewq4iwdhm" />
        </Field>
        <Field label="Capital financiable máximo ($)" htmlFor={`${p}-cap`} data-id="element-196" data-mp="1lgcrzmphik7z">
          <NumberInput id={`${p}-cap`} value={value.maxFinanceAmount} onChange={v => set('maxFinanceAmount', v)} allowNull placeholder="Sin tope" data-id="element-197" data-mp="1qghafi9ydp0u" />
        </Field>
        <Field label="Seguro mensual ($)" htmlFor={`${p}-ins`} data-id="element-198" data-mp="1vdhgvuapstsh">
          <NumberInput id={`${p}-ins`} value={value.monthlyInsurance} onChange={v => set('monthlyInsurance', v)} allowNull data-id="element-199" data-mp="3s9onsm609wk3" />
        </Field>
        <Field label="Gastos de otorgamiento ($)" htmlFor={`${p}-fees`} data-id="element-200" data-mp="2ec2i4rkk6u4f">
          <NumberInput id={`${p}-fees`} value={value.adminFees} onChange={v => set('adminFees', v)} allowNull data-id="element-201" data-mp="2ee6uzz0fr3gu" />
        </Field>
        <Field label="Entidad otorgante" htmlFor={`${p}-lender`} data-id="element-202" data-mp="25jiradaidtu5">
          <input id={`${p}-lender`} className="input" value={value.lender} onChange={e => set('lender', e.target.value)} maxLength={80} data-id="element-203" data-mp="25k0n58t9e61j" />
        </Field>
        <Field label="Vigencia hasta" htmlFor={`${p}-valid`} data-id="element-204" data-mp="2xb849w10vpql">
          <input id={`${p}-valid`} type="date" className="input" value={value.validUntil} onChange={e => set('validUntil', e.target.value)} data-id="element-205" data-mp="2x8lstlb0gq33" />
        </Field>
      </div>

      <Field label="Promoción especial (opcional)" htmlFor={`${p}-promo`} hint="Sólo se publica si la oferta está verificada y vigente." data-id="element-206" data-mp="2o8f9vjwjmxwj">
        <input id={`${p}-promo`} className="input" value={value.promo ?? ''} onChange={e => set('promo', e.target.value || null)} maxLength={120} data-id="element-207" data-mp="1cutmo4a8v6dn" />
      </Field>
      <Field label="Impuestos aplicables" htmlFor={`${p}-tax`} data-id="element-208" data-mp="17up50axlo5lu">
        <input id={`${p}-tax`} className="input" value={value.taxNote} onChange={e => set('taxNote', e.target.value)} maxLength={200} data-id="element-209" data-mp="2vrn8kg6vyqnx" />
      </Field>
      <Field label="Condiciones de aprobación" htmlFor={`${p}-cond`} data-id="element-210" data-mp="30l9c9iv2nk7l">
        <textarea id={`${p}-cond`} className="textarea" rows={2} value={value.conditions} onChange={e => set('conditions', e.target.value)} maxLength={400} data-id="element-211" data-mp="2mtl23l4dv5t9" />
      </Field>

      <label className={`flex items-start gap-3 rounded-xl border p-3 text-sm ${value.verified ? 'border-ok/40 bg-ok/5' : 'border-warn/30 bg-warn/5'}`} data-id="element-212" data-mp="3fhqlo4jpaori">
        <input type="checkbox" checked={value.verified} onChange={e => set('verified', e.target.checked)} className="mt-0.5 h-4 w-4 accent-[#34D399]" data-id="element-213" data-mp="3jfy1mx6cbm1n" />
        <span data-id="element-214" data-mp="3agazua9p8e82">
          <span className="font-semibold" data-id="element-215" data-mp="3ag18ojcx410s">Condiciones verificadas con la entidad otorgante</span>
          <span className="block text-xs text-mist" data-id="element-216" data-mp="1u1rlhskewibj">
            Mientras no esté marcada, el sitio la muestra como “simulación referencial” y no publica la promoción.
          </span>
        </span>
      </label>
    </div>;
}